/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CheckCircle2, XCircle, Clock, Send, RefreshCw, FileDown, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_META = {
  aceptado:   { color: "bg-success/15 text-success",         Icon: CheckCircle2, label: "Aceptado" },
  en_proceso: { color: "bg-amber-500/15 text-amber-600",     Icon: Clock,        label: "En proceso" },
  pendiente_upload_manual: {
    color: "bg-info/15 text-info", Icon: Clock,
    label: "Listo para subir manual",
  },
  rechazado:  { color: "bg-destructive/15 text-destructive", Icon: XCircle,      label: "Rechazado" },
  rechazado_rfce: { color: "bg-destructive/15 text-destructive", Icon: XCircle, label: "RFCE rechazado" },
  error:      { color: "bg-destructive/15 text-destructive", Icon: XCircle,      label: "Error" },
  enviado:    { color: "bg-muted text-muted-foreground",     Icon: Clock,        label: "Enviado" },
};

export default function Phase4CertificationTab() {
  const [status, setStatus] = useState(null);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null); // {it, live, loading, error}

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/dgii/phase4/status"),
        api.get("/dgii/phase4/sent"),
      ]);
      setStatus(s.data);
      setSent(l.data.items || []);
    } catch (e) {
      toast.error("No se pudo cargar el estado: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const sendRfceBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dgii/phase4/rfce-batch");
      toast.success(`RFCE enviadas: ${data.ok}/${data.total}`);
      await refresh();
    } catch (e) {
      toast.error("Error enviando RFCE: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dgii/phase4/refresh-status");
      toast.success(`Estados actualizados: ${data.updated}`);
      await refresh();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (it) => {
    setDetail({ it, loading: true, live: null, error: null });
    try {
      const { data } = await api.get(`/dgii/phase4/raw-status/${it.scope}/${it.id}`);
      setDetail({ it, loading: false, live: data, error: null });
    } catch (e) {
      setDetail({ it, loading: false, live: null,
                  error: e.response?.data?.detail || e.message });
    }
  };

  const overallPct = status
    ? Math.round((status.total_sent / status.total_needed) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="phase4-tab">
      <Card className="border-info/40 bg-info/5">
        <CardContent className="pt-5 text-sm space-y-2">
          <p className="font-semibold text-foreground">Orden de emisión obligatorio (DGII)</p>
          <ol className="list-decimal pl-5 space-y-0.5 text-muted-foreground">
            <li><strong>Primero</strong>: tipos 31, 32≥250K, 41, 43, 44, 45, 46, 47 (envío directo vía API).</li>
            <li><strong>Segundo</strong>: 33 Nota Débito y 34 Nota Crédito.</li>
            <li><strong>Tercero</strong>: Resumen de Factura de Consumo &lt;250K (RFCE, vía API).</li>
            <li><strong>Cuarto</strong>: subir manualmente las E32&lt;250K en el portal DGII (sólo luego de que su RFCE sea aceptado).</li>
          </ol>
          <p className="text-xs mt-2 text-muted-foreground">
            Usa el botón <strong>"Enviar e-CF"</strong> en Ventas/Compras/Notas — nosotros apuntamos al endpoint correcto según el tipo.
            Para las E32&lt;250K, cuando el RFCE sea aceptado el estado cambia a <strong>pendiente_upload_manual</strong>:
            descarga el XML firmado y súbelo en el portal DGII → "Facturas de consumo &lt; 250Mil".
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-xl">Paso 4: Pruebas de Simulación e-CF</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Emite comprobantes reales desde Ventas, Compras y Notas apuntando al ambiente
                TesteCF. El contador abajo refleja el progreso contra la DGII (25 e-CF requeridos).
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={refreshStatus} disabled={busy}
                      data-testid="phase4-refresh-status-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refrescar estados DGII
              </Button>
              <Button variant="outline" size="sm" onClick={refresh} disabled={loading}
                      data-testid="phase4-reload-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Recargar
              </Button>
              <a href="/api/dgii/phase4/representation/bulk.zip" target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="phase4-bulk-zip-btn">
                  <FileDown className="w-4 h-4 mr-1" /> ZIP representaciones
                </Button>
              </a>
              <Button size="sm" onClick={sendRfceBatch} disabled={busy}
                      data-testid="phase4-rfce-batch-btn">
                <Send className="w-4 h-4 mr-1" /> Enviar RFCE pendientes
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  Progreso general: {status.total_sent} / {status.total_needed} ({overallPct}%)
                </span>
                {status.complete && (
                  <Badge className="bg-success/15 text-success">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Completo
                  </Badge>
                )}
              </div>
              <Progress value={overallPct} data-testid="phase4-overall-progress" />
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead className="text-right">Progreso</TableHead>
                <TableHead className="text-right">Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(status?.rows || []).map(r => (
                <TableRow key={r.key} data-testid={`phase4-row-${r.key}`}>
                  <TableCell className="font-mono text-sm">E{r.key.replace("_RFCE", " RFCE")}</TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.scope === "sale" ? "POS / Ventas" : r.scope === "purchase" ? "Compras" : "Notas"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {r.sent} / {r.needed}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.complete
                      ? <Badge className="bg-success/15 text-success"><CheckCircle2 className="w-3 h-3 mr-1" /> OK</Badge>
                      : <Badge variant="outline">Pendiente</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimos e-CF enviados</CardTitle>
        </CardHeader>
        <CardContent>
          {sent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay e-CF enviados. Empieza desde el POS, Compras o Notas.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>eNCF</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Contraparte</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>TrackId</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sent.slice(0, 50).map(it => {
                  const meta = STATUS_META[it.ecf_status] || STATUS_META.enviado;
                  const Icon = meta.Icon;
                  const downloadUrl =
                    it.scope === "invoice" ? `/api/invoices/${it.id}/ecf-xml` :
                    it.scope === "purchase" ? `/api/purchases/${it.id}/ecf-xml` :
                    `/api/credit-debit-notes/${it.id}/ecf-xml`;
                  const pdfUrl = `/api/dgii/phase4/representation/${it.scope}/${it.id}.pdf`;
                  const htmlUrl = `/api/dgii/phase4/representation/${it.scope}/${it.id}.html`;
                  return (
                    <TableRow key={`${it.scope}-${it.id}`}>
                      <TableCell className="font-mono text-xs">{it.ncf}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{it.scope}</TableCell>
                      <TableCell className="text-sm">{it.customer_name || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {Number(it.total || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.ecf_track_id?.slice(0, 12) || "—"}</TableCell>
                      <TableCell>
                        <Badge className={meta.color}>
                          <Icon className="w-3 h-3 mr-1" /> {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="sm"
                                  onClick={() => openDetail(it)}
                                  title="Ver respuesta DGII"
                                  data-testid={`phase4-detail-${it.id}`}>
                            <Info className="w-3.5 h-3.5" />
                          </Button>
                          <a href={downloadUrl} target="_blank" rel="noreferrer" title="XML firmado">
                            <Button variant="ghost" size="sm" data-testid={`phase4-xml-${it.id}`}>
                              <FileDown className="w-3.5 h-3.5" />
                            </Button>
                          </a>
                          <a href={pdfUrl} target="_blank" rel="noreferrer" title="PDF con QR DGII">
                            <Button variant="ghost" size="sm" data-testid={`phase4-pdf-${it.id}`}>PDF</Button>
                          </a>
                          <a href={htmlUrl} target="_blank" rel="noreferrer" title="Vista imprimible">
                            <Button variant="ghost" size="sm" data-testid={`phase4-html-${it.id}`}>HTML</Button>
                          </a>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-base">
              Respuesta DGII — {detail?.it?.ncf}
            </DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <p className="text-sm text-muted-foreground">Consultando DGII…</p>
          ) : detail?.error ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {detail.error}
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">TrackId:</span>
                  <span className="font-mono ml-1 text-xs break-all">{detail?.live?.track_id || "—"}</span></div>
                <div><span className="text-muted-foreground">Estado interno:</span>
                  <span className="ml-1 font-medium">{detail?.live?.internal_status || "—"}</span></div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Respuesta en vivo de DGII (ConsultaResultado)
                </p>
                <pre className="bg-muted/50 rounded p-3 text-xs overflow-x-auto max-h-72 whitespace-pre-wrap break-all">
{detail?.live?.live_dgii_response
  ? JSON.stringify(detail.live.live_dgii_response, null, 2)
  : (detail?.live?.live_error || "— (sin respuesta en vivo)")}
                </pre>
              </div>
              {detail?.live?.stored_dgii_response && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">
                    Última respuesta guardada en BD
                  </p>
                  <pre className="bg-muted/50 rounded p-3 text-xs overflow-x-auto max-h-56 whitespace-pre-wrap break-all">
{JSON.stringify(detail.live.stored_dgii_response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
